import React, { useState, useEffect, useCallback } from 'react';
import './AppVersionPage.css';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://api.beetlestore.online').replace(/\/$/, '');
const API_KEY     = process.env.REACT_APP_BACKEND_API_KEY || '';
const HEADERS     = { 'Content-Type': 'application/json', 'X-API-Key': API_KEY };

async function apiFetch(path, opts = {}) {
  const res  = await fetch(`${BACKEND_URL}${path}`, { headers: HEADERS, ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
}

const APPS = [
  {
    id:    'customer',
    label: 'Customer App',
    desc:  'Beetle – customer shopping app',
    pkg:   'store.beetletz.customer',
    color: '#039855',
    icon:  '🛒',
  },
  {
    id:    'driver',
    label: 'Driver App',
    desc:  'Beetle Driver – delivery partner app',
    pkg:   'store.beetletz.driver',
    color: '#0284c7',
    icon:  '🚚',
  },
  {
    id:    'shop_owner',
    label: 'Shop Owner App',
    desc:  'Beetle Shop – merchant app for store owners',
    pkg:   'store.beetletz.shopowner',
    color: '#7c3aed',
    icon:  '🏪',
  },
];

const EMPTY = {
  latestVersion:   '',
  latestBuild:     '',
  forceUpdate:     false,
  playStoreUrl:    '',
  releaseNotes:    '',
};

// ─── App Version Card ─────────────────────────────────────────────────────────

const AppVersionCard = ({ app }) => {
  const [form,    setForm]    = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [toast,   setToast]   = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(`/api/version/check?app=${app.id}&build=0`);
      setForm({
        latestVersion: data.latestVersion || '',
        latestBuild:   data.latestBuild   || '',
        forceUpdate:   data.forceUpdate   || false,
        playStoreUrl:  data.playStoreUrl  || '',
        releaseNotes:  data.releaseNotes  || '',
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [app.id]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.latestVersion.trim()) { setError('Version is required'); return; }
    const build = parseInt(form.latestBuild, 10);
    if (!build || build < 1)        { setError('Build number must be a positive integer'); return; }

    setSaving(true);
    setError(null);
    try {
      await apiFetch(`/api/version/${app.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          latestVersion: form.latestVersion.trim(),
          latestBuild:   build,
          forceUpdate:   form.forceUpdate,
          playStoreUrl:  form.playStoreUrl.trim(),
          releaseNotes:  form.releaseNotes.trim(),
        }),
      });
      showToast(`${app.label} version updated successfully`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="av-card">
      {/* Card header */}
      <div className="av-card-header">
        <div className="av-card-icon" style={{ background: `${app.color}18`, color: app.color }}>
          <span>{app.icon}</span>
        </div>
        <div>
          <h3 className="av-card-title">{app.label}</h3>
          <p className="av-card-desc">{app.desc}</p>
          <code className="av-card-pkg">{app.pkg}</code>
        </div>
      </div>

      {loading ? (
        <div className="av-loading"><div className="av-spinner" /></div>
      ) : (
        <form className="av-form" onSubmit={handleSave}>
          {error && <div className="av-alert av-alert-error">{error}</div>}
          {toast && <div className={`av-alert av-alert-${toast.type}`}>{toast.msg}</div>}

          <div className="av-form-grid">
            {/* Version */}
            <div className="av-field">
              <label>Latest Version</label>
              <input
                value={form.latestVersion}
                onChange={e => set('latestVersion', e.target.value)}
                placeholder="e.g. 1.0.1"
              />
            </div>

            {/* Build */}
            <div className="av-field">
              <label>Latest Build Number</label>
              <input
                type="number"
                min="1"
                value={form.latestBuild}
                onChange={e => set('latestBuild', e.target.value)}
                placeholder="e.g. 9"
              />
            </div>

            {/* Play Store URL */}
            <div className="av-field av-field-wide">
              <label>Play Store URL</label>
              <input
                value={form.playStoreUrl}
                onChange={e => set('playStoreUrl', e.target.value)}
                placeholder="https://play.google.com/store/apps/details?id=..."
              />
            </div>

            {/* Release Notes */}
            <div className="av-field av-field-wide">
              <label>Release Notes</label>
              <textarea
                value={form.releaseNotes}
                onChange={e => set('releaseNotes', e.target.value)}
                placeholder="What's new in this version..."
                rows={3}
              />
            </div>

            {/* Force Update toggle */}
            <div className="av-field av-field-wide">
              <div
                className={`av-toggle-row ${form.forceUpdate ? 'av-toggle-on' : ''}`}
                onClick={() => set('forceUpdate', !form.forceUpdate)}
              >
                <div className="av-toggle-info">
                  <div className="av-toggle-label">Force Update</div>
                  <div className="av-toggle-desc">
                    When enabled, users must update before using the app. They cannot skip this.
                  </div>
                </div>
                <div className={`av-switch ${form.forceUpdate ? 'on' : ''}`}>
                  <div className="av-switch-thumb" />
                </div>
              </div>
              {form.forceUpdate && (
                <div className="av-force-warning">
                  Force update is ON — all users will be blocked until they update.
                </div>
              )}
            </div>
          </div>

          <div className="av-form-actions">
            <button type="button" className="av-btn-secondary" onClick={load} disabled={saving}>
              Reset
            </button>
            <button type="submit" className="av-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────

const AppVersionPage = () => {
  return (
    <div className="av-page">
      <div className="av-page-header">
        <h1>App Version Management</h1>
        <p className="av-page-sub">
          Control version requirements and force-update behaviour for all Beetle mobile apps.
        </p>
      </div>

      <div className="av-info-banner">
        <span className="av-info-icon">ℹ️</span>
        <span>
          Set <strong>Force Update ON</strong> to block users on older builds until they update.
          Users on the latest build are unaffected. Changes take effect immediately on next app launch.
        </span>
      </div>

      <div className="av-cards">
        {APPS.map(app => (
          <AppVersionCard key={app.id} app={app} />
        ))}
      </div>
    </div>
  );
};

export default AppVersionPage;
